
import { useState, useEffect } from 'react';

interface ContactCard {
  icon: string;
  title: string;
  details: string[];
  action: string;
  link: string;
  color: string;
}

export default function QuickContactCards() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('quick-contact');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const contactCards: ContactCard[] = [
    {
      icon: 'ri-map-pin-line',
      title: 'Visit Our Office',
      details: [
        '8300 Douglas Ave Ste 800',
        'Dallas, TX 75225'
      ],
      action: 'Get Directions',
      link: 'https://maps.google.com/?q=8300+Douglas+Ave+Ste+800+Dallas+TX+75225',
      color: 'bg-secondary'
    },
    {
      icon: 'ri-phone-line',
      title: 'Call Us',
      details: [
        '(214) 306-0121',
        'Free consultation available'
      ],
      action: 'Call Now',
      link: 'tel:+12143060121',
      color: 'bg-secondary'
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Us',
      details: [
        'info@dallashealthinsurance.com',
        'We respond within 24 hours'
      ],
      action: 'Send Email',
      link: 'mailto:info@dallashealthinsurance.com',
      color: 'bg-secondary'
    },
    {
      icon: 'ri-time-line',
      title: 'Office Hours',
      details: [
        'Mon-Fri: 8:00 AM - 6:00 PM',
        'Saturday: 9:00 AM - 2:00 PM'
      ],
      action: 'Schedule Appointment',
      link: '/contact',
      color: 'bg-secondary'
    }
  ];

  return (
    <section id="quick-contact" className="py-20 bg-gradient-to-b from-white to-lightBlue/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
            Get In Touch
          </h2>
          <p className="text-lg text-darkGray max-w-3xl mx-auto">
            Multiple ways to connect with our team — choose what works best for you
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {contactCards.map((card, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl p-8 border border-lightBlue/20 hover:shadow-xl transition-all duration-700 ${
                isVisible
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className={`w-16 h-16 rounded-lg ${card.color} flex items-center justify-center mb-6`}>
                <i className={`${card.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="font-semibold text-xl text-primary mb-4">
                {card.title}
              </h3>
              <div className="space-y-2 mb-6">
                {card.details.map((detail, idx) => (
                  <p key={idx} className="text-darkGray">
                    {detail}
                  </p>
                ))}
              </div>
              <a
                href={card.link}
                target={card.link.startsWith('http') ? '_blank' : undefined}
                rel={card.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="inline-flex items-center gap-2 text-secondary font-semibold hover:gap-3 transition-all cursor-pointer"
              >
                <span>{card.action}</span>
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
